import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Users,
  Car,
  Package,
  AlertCircle,
  CreditCard,
  Receipt,
  Target,
  Zap,
  Award,
  ChevronUp,
  ChevronDown
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-500" size={20} />
          <h3 className="text-lg font-semibold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <ChevronUp className="text-blue-500" size={20} /> : <ChevronDown className="text-blue-500" size={20} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-800 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const RevenueBreakdownReport = () => {
  const [trips, setTrips] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [rideshareTrips, setRideshareTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({});

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null;
    }
  };

  // The platform's real product split within `/trip` (private-ride vs delivery) — `service_type`
  // is the authoritative field (`routes/trip.js` sets it server-side as
  // `postedValues.service_type || (deliveray_details ? "delivery" : "private_ride")`), but older
  // rows created before that field existed may not have it, so fall back to the same signals
  // other screens in this codebase already use for those rows.
  const isDeliveryTrip = (trip) => {
    if (trip.service_type) return trip.service_type === 'delivery';
    if (trip.deliveray_details) return true;
    return (parseFloat(trip.weight) || 0) > 0;
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [tripsRes, rideshareRequestsRes, rideshareTripsRes, driversRes, customersRes] = await Promise.all([
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/rideshare/requests/all`),
        fetch(`${API_URL}/rideshare/trips`),
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/customerdetails/`)
      ]);

      const [tripsData, rideshareRequestsData, rideshareTripsData, driversData, customersData] = await Promise.all([
        tripsRes.json(),
        rideshareRequestsRes.json(),
        rideshareTripsRes.json(),
        driversRes.json(),
        customersRes.json()
      ]);

      setTrips(tripsData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setRideshareTrips(Array.isArray(rideshareTripsData) ? rideshareTripsData : []);
      setDrivers(driversData || []);
      setCustomers(customersData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Process revenue data
  const revenueAnalysis = useMemo(() => {
    const dateRange = getDateRange(timeFilter);
    // `/trip` is exclusively the private-ride/delivery table — everything in it is a private trip,
    // there's no `trip_type`/`rideshare_id` field on these rows to filter by.
    const privateTrips = filterTripsByDate(trips, dateRange);

    // Rideshare revenue lives on accepted `rideshare_requests` rows (one per passenger,
    // `accepted_amount` is the fare-of-record), not on `/trip`. Join each request back to its
    // route (`rideshare_trips`) to get the driver who gets credit for that fare.
    const driverIdByRideshareId = {};
    rideshareTrips.forEach(trip => {
      driverIdByRideshareId[trip.rideshare_id] = trip.driver_id;
    });

    const filteredRideshareRequests = (dateRange
      ? rideshareRequests.filter(req => {
          if (!req.created_at) return false;
          const d = new Date(req.created_at);
          return d >= dateRange.start && d <= dateRange.end;
        })
      : rideshareRequests
    ).filter(req => (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0) > 0);

    // Initialize revenue data structures — three real product lines (Private Rides, Deliveries,
    // Shared Rides), each tracked separately so their individual performance is visible, not just
    // a "private vs rideshare" composite.
    const revenueByServiceType = {
      privateRide: { count: 0, revenue: 0, avgRevenue: 0 },
      delivery: { count: 0, revenue: 0, avgRevenue: 0 },
      rideshare: { count: 0, revenue: 0, avgRevenue: 0 }
    };

    const revenueByPriceRange = {
      '0-10': { count: 0, revenue: 0, avgRevenue: 0 },
      '10-25': { count: 0, revenue: 0, avgRevenue: 0 },
      '25-50': { count: 0, revenue: 0, avgRevenue: 0 },
      '50-100': { count: 0, revenue: 0, avgRevenue: 0 },
      '100+': { count: 0, revenue: 0, avgRevenue: 0 }
    };

    const revenueByHour = {};
    const revenueByDay = {};
    const revenueByDriver = {};
    const revenueByCustomer = {};

    // Shared accumulator so private trips and rideshare requests land in the same buckets.
    const accumulate = (serviceType, revenue, date, driverId, customerId) => {
      revenueByServiceType[serviceType].count++;
      revenueByServiceType[serviceType].revenue += revenue;

      let priceRange;
      if (revenue < 10) priceRange = '0-10';
      else if (revenue < 25) priceRange = '10-25';
      else if (revenue < 50) priceRange = '25-50';
      else if (revenue < 100) priceRange = '50-100';
      else priceRange = '100+';
      revenueByPriceRange[priceRange].count++;
      revenueByPriceRange[priceRange].revenue += revenue;

      if (date && !isNaN(date.getTime())) {
        const hour = date.getHours();
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[date.getDay()];

        if (!revenueByHour[hour]) revenueByHour[hour] = { count: 0, revenue: 0, avgRevenue: 0 };
        revenueByHour[hour].count++;
        revenueByHour[hour].revenue += revenue;

        if (!revenueByDay[dayName]) revenueByDay[dayName] = { count: 0, revenue: 0, avgRevenue: 0 };
        revenueByDay[dayName].count++;
        revenueByDay[dayName].revenue += revenue;
      }

      if (driverId) {
        if (!revenueByDriver[driverId]) {
          revenueByDriver[driverId] = { count: 0, revenue: 0, avgRevenue: 0, driverInfo: null };
        }
        revenueByDriver[driverId].count++;
        revenueByDriver[driverId].revenue += revenue;
      }

      if (customerId) {
        if (!revenueByCustomer[customerId]) {
          revenueByCustomer[customerId] = { count: 0, revenue: 0, avgRevenue: 0, customerInfo: null };
        }
        revenueByCustomer[customerId].count++;
        revenueByCustomer[customerId].revenue += revenue;
      }
    };

    privateTrips.forEach(trip => {
      const revenue = parseFloat(trip.delivery_cost_proposed) || 0;
      const serviceType = isDeliveryTrip(trip) ? 'delivery' : 'privateRide';
      accumulate(serviceType, revenue, new Date(trip.order_start_datetime), trip.driver_id, trip.cust_id);
    });

    filteredRideshareRequests.forEach(req => {
      const revenue = parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0;
      const driverId = driverIdByRideshareId[req.rideshare_id];
      accumulate('rideshare', revenue, req.created_at ? new Date(req.created_at) : null, driverId, req.passenger_id);
    });

    // Add driver and customer info with better matching
    drivers.forEach(driver => {
      // Try multiple ID fields for matching
      const driverIds = [
        driver.driver_id,
        driver.id,
        driver._id,
        driver.driverid,
        driver.driverId
      ].filter(id => id); // Remove null/undefined

      driverIds.forEach(driverId => {
        if (revenueByDriver[driverId]) {
          revenueByDriver[driverId].driverInfo = driver;
        }
      });
    });

    customers.forEach(customer => {
      // Try multiple ID fields for matching - prioritize customerid (also how rideshare's
      // passenger_id is keyed, so this same match covers rideshare-derived entries too)
      const customerIds = [
        customer.customerid,
        customer.cust_id,
        customer.id,
        customer._id,
        customer.customerId,
        customer.user_id,
        customer.userId
      ].filter(id => id); // Remove null/undefined

      customerIds.forEach(customerId => {
        if (revenueByCustomer[customerId]) {
          revenueByCustomer[customerId].customerInfo = customer;
        }
      });
    });

    // Calculate averages
    Object.keys(revenueByServiceType).forEach(type => {
      const data = revenueByServiceType[type];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByPriceRange).forEach(range => {
      const data = revenueByPriceRange[range];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByHour).forEach(hour => {
      const data = revenueByHour[hour];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByDay).forEach(day => {
      const data = revenueByDay[day];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByDriver).forEach(driverId => {
      const data = revenueByDriver[driverId];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByCustomer).forEach(customerId => {
      const data = revenueByCustomer[customerId];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    return {
      privateTrips,
      filteredRideshareRequests,
      revenueByServiceType,
      revenueByPriceRange,
      revenueByHour,
      revenueByDay,
      revenueByDriver,
      revenueByCustomer,
      totalRevenue: revenueByServiceType.privateRide.revenue + revenueByServiceType.delivery.revenue + revenueByServiceType.rideshare.revenue,
      totalCount: revenueByServiceType.privateRide.count + revenueByServiceType.delivery.count + revenueByServiceType.rideshare.count
    };
  }, [trips, rideshareRequests, rideshareTrips, drivers, customers, timeFilter]);

  // Get top performers
  const getTopPerformers = (data, metric, limit = 20) => {
    return Object.entries(data)
      .map(([id, stats]) => ({ id, ...stats }))
      .sort((a, b) => b[metric] - a[metric])
      .slice(0, limit);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename, includeId = false) => {
    const headers = includeId 
      ? ['ID', 'Name', 'Email', 'Phone', 'Trips', 'Revenue', 'Average Revenue']
      : ['Category', 'Trips', 'Revenue', 'Average Revenue'];
    
    const csv = [
      headers,
      ...data.map(item => {
        if (includeId) {
          const info = item.driverInfo || item.customerInfo;
          return [
            item.id,
            info?.name ? `${info.name} ${info.surname || ''}`.trim() : `${info?.first_name || ''} ${info?.last_name || ''}`.trim() || 'Unknown',
            info?.email || 'N/A',
            info?.phone || 'N/A',
            item.count,
            item.revenue?.toFixed(2) || 0,
            item.avgRevenue?.toFixed(2) || 0
          ];
        } else {
          return [
            item.name || item.id || 'Unknown',
            item.count || 0,
            item.revenue?.toFixed(2) || 0,
            item.avgRevenue?.toFixed(2) || 0
          ];
        }
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const {
    revenueByServiceType,
    revenueByPriceRange,
    revenueByHour,
    revenueByDay,
    revenueByDriver,
    revenueByCustomer,
    totalRevenue,
    totalCount
  } = revenueAnalysis;

  const topDriversByRevenue = getTopPerformers(revenueByDriver, 'revenue');
  const topCustomersByRevenue = getTopPerformers(revenueByCustomer, 'revenue');

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Revenue Breakdown Report
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive revenue analysis by service type, price range, time, and customer segments
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <DollarSign className="text-gray-500" size={20} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getFilterLabel(timeFilter)}
              </span>
            </div>
          </div>

          {/* Time Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "7days", label: "Past 7 Days" },
              { value: "14days", label: "Past 14 Days" },
              { value: "30days", label: "Past 30 Days" }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  timeFilter === filter.value
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Report Tooltip */}
        <ReportTooltip title="Revenue Breakdown Report - Business Value & Features">
          <div className="space-y-3">
            <div>
              <strong className="text-blue-900">💰 Report Overview:</strong>
              <p>Comprehensive revenue analysis by service type, price range, time patterns, and customer segments with detailed financial insights.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🎯 Key Business Questions Answered:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Revenue Optimization:</strong> Which service generates more revenue? What price ranges drive most income? When do we make most money?</li>
                <li><strong>Customer Value:</strong> Who are my most valuable customers? Which customers should I retain? Customer lifetime value analysis?</li>
                <li><strong>Driver Performance:</strong> Which drivers generate most revenue? Who are my top revenue drivers? Driver financial performance?</li>
                <li><strong>Strategic Planning:</strong> How is revenue distributed? What are the revenue patterns? Where should I focus resources?</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">✅ Key Features:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Product Line Analysis:</strong> Private Rides, Deliveries, and Shared Rides tracked individually, alongside the composite total</li>
                <li><strong>Price Range Segments:</strong> Revenue distribution by trip value ranges ($0-10, $10-25, $25-50, $50-100, $100+)</li>
                <li><strong>Time-Based Revenue:</strong> Hourly and daily revenue patterns with visual heatmaps</li>
                <li><strong>Top Performers:</strong> Highest revenue drivers and customers with detailed profiles</li>
                <li><strong>Export Functionality:</strong> CSV download for all revenue data and analysis</li>
                <li><strong>Real-Time Data:</strong> Live API calls with 30-second refresh intervals</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">💼 Business Impact:</strong>
              <p>This report provides immediate financial value by helping you optimize pricing strategies, identify high-value customers, reward top-performing drivers, maximize revenue during peak times, and make data-driven financial decisions.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🔍 Data Source:</strong>
              <p>Uses live data from the trips, rideshare requests, drivers, and customers endpoints. Private-ride and delivery revenue is each trip's <code>delivery_cost_proposed</code> (split by the trip's real <code>service_type</code>); shared-ride revenue is each accepted passenger request's agreed fare.</p>
            </div>
          </div>
        </ReportTooltip>

        {/* Summary Cards — composite total first, then each product line's individual performance */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow xl:col-span-1 ring-2 ring-gray-200 dark:ring-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Revenue {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${totalRevenue.toFixed(0)}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">All 3 products, composite</p>
              </div>
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Private Ride Revenue</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  ${revenueByServiceType.privateRide.revenue.toFixed(0)}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{revenueByServiceType.privateRide.count} trips</p>
              </div>
              <Car className="text-blue-500" size={24} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Delivery Revenue</p>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  ${revenueByServiceType.delivery.revenue.toFixed(0)}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{revenueByServiceType.delivery.count} trips</p>
              </div>
              <Package className="text-amber-500" size={24} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Shared Ride Revenue</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  ${revenueByServiceType.rideshare.revenue.toFixed(0)}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{revenueByServiceType.rideshare.count} trips</p>
              </div>
              <Users className="text-purple-500" size={24} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Trip Value</p>
                <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  ${(totalCount > 0 ? totalRevenue / totalCount : 0).toFixed(2)}
                </p>
              </div>
              <Receipt className="text-gray-500" size={24} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalCount}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {revenueByServiceType.privateRide.count} private &middot; {revenueByServiceType.delivery.count} delivery &middot; {revenueByServiceType.rideshare.count} shared
                </p>
              </div>
              <Activity className="text-gray-500" size={24} />
            </div>
          </div>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Revenue by Product Line — Private Rides, Deliveries, and Shared Rides are three
              distinct products; each gets its own individual performance card, alongside the
              composite Total Revenue card above. */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <PieChart className="mr-2 text-green-500" />
              Revenue by Product Line
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    const productMeta = {
                      privateRide: { label: 'Private Rides', color: 'blue', Icon: Car },
                      delivery: { label: 'Deliveries', color: 'amber', Icon: Package },
                      rideshare: { label: 'Shared Rides', color: 'purple', Icon: Users }
                    };
                    return Object.entries(revenueByServiceType).map(([type, data]) => {
                      const percentage = totalRevenue > 0 ? (data.revenue / totalRevenue * 100) : 0;
                      const { label, color, Icon } = productMeta[type] || { label: type, color: 'gray', Icon: AlertCircle };

                      return (
                        <div key={type} className="border rounded-lg p-4 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Icon className={`text-${color}-500`} size={20} />
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {label}
                              </span>
                            </div>
                            <span className={`bg-${color}-100 text-${color}-800 px-2 py-1 rounded text-sm font-medium`}>
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <p>Revenue: ${data.revenue.toFixed(2)}</p>
                            <p>Trips: {data.count}</p>
                            <p>Avg: ${data.avgRevenue.toFixed(2)}</p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                  Percentages are each product's share of the ${totalRevenue.toFixed(0)} composite total above.
                </p>
              </div>
            </div>
          </div>

          {/* Revenue by Price Range */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Target className="mr-2 text-purple-500" />
                Revenue by Price Range
              </div>
              <button
                onClick={() => exportData(Object.entries(revenueByPriceRange).map(([name, data]) => ({ name, ...data })), 'revenue-by-price-range.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(revenueByPriceRange).map(([range, data]) => {
                    const percentage = totalRevenue > 0 ? (data.revenue / totalRevenue * 100) : 0;
                    const colors = {
                      '0-10': 'green',
                      '10-25': 'blue',
                      '25-50': 'purple',
                      '50-100': 'orange',
                      '100+': 'red'
                    };
                    const color = colors[range];
                    
                    return (
                      <div key={range} className="border rounded-lg p-4 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <DollarSign className={`text-${color}-500`} size={20} />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              ${range}
                            </span>
                          </div>
                          <span className={`bg-${color}-100 text-${color}-800 px-2 py-1 rounded text-sm font-medium`}>
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Revenue: ${data.revenue.toFixed(2)}</p>
                          <p>Trips: {data.count}</p>
                          <p>Avg: ${data.avgRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Top Revenue Drivers */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Award className="mr-2 text-yellow-500" />
                Top Revenue Drivers
              </div>
              <button
                onClick={() => exportData(topDriversByRevenue, 'top-revenue-drivers.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Driver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topDriversByRevenue.map((driver, index) => (
                      <tr key={driver.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                            #{index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {driver.driverInfo?.name || `${driver.driverInfo?.first_name || ''} ${driver.driverInfo?.last_name || ''}`.trim() || 
                           `Driver #${driver.id}` || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {driver.driverInfo?.email || driver.driverInfo?.phone || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{driver.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${driver.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${driver.avgRevenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Revenue Customers */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Award className="mr-2 text-blue-500" />
                Top Revenue Customers
              </div>
              <button
                onClick={() => exportData(topCustomersByRevenue, 'top-revenue-customers.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topCustomersByRevenue.map((customer, index) => (
                      <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                            #{index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {customer.customerInfo?.name ? `${customer.customerInfo.name} ${customer.customerInfo.surname || ''}`.trim() : 
                           `${customer.customerInfo?.first_name || ''} ${customer.customerInfo?.last_name || ''}`.trim() || 
                           `Customer #${customer.id}` || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {customer.customerInfo?.email || customer.customerInfo?.phone || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{customer.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${customer.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${customer.avgRevenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Revenue by Hour */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Zap className="mr-2 text-yellow-500" />
              Revenue by Hour
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const hourData = revenueByHour[hour];
                    const revenue = hourData ? hourData.revenue : 0;
                    const maxRevenue = Math.max(...Object.values(revenueByHour).map(h => h.revenue));
                    const intensity = maxRevenue > 0 ? (revenue / maxRevenue) : 0;
                    
                    let bgColor = 'bg-gray-100';
                    if (intensity > 0.8) bgColor = 'bg-green-500';
                    else if (intensity > 0.6) bgColor = 'bg-green-400';
                    else if (intensity > 0.4) bgColor = 'bg-yellow-400';
                    else if (intensity > 0.2) bgColor = 'bg-orange-400';
                    else if (intensity > 0) bgColor = 'bg-red-400';
                    
                    return (
                      <div
                        key={hour}
                        className={`${bgColor} text-white rounded p-2 text-center`}
                        title={`${hour}:00 - $${revenue.toFixed(2)}`}
                      >
                        <div className="text-xs font-medium">{hour}:00</div>
                        <div className="text-xs">${revenue.toFixed(0)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center space-x-4 mt-4 text-xs text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-gray-100 rounded"></div>
                    <span>No Revenue</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-red-400 rounded"></div>
                    <span>Low</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-orange-400 rounded"></div>
                    <span>Medium</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-yellow-400 rounded"></div>
                    <span>High</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-400 rounded"></div>
                    <span>Very High</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span>Peak</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue by Day */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Calendar className="mr-2 text-indigo-500" />
              Revenue by Day
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(revenueByDay).map(([day, data]) => {
                    const percentage = totalRevenue > 0 ? (data.revenue / totalRevenue * 100) : 0;
                    
                    return (
                      <div key={day} className="border rounded-lg p-4 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {day}
                          </span>
                          <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-sm font-medium">
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Revenue: ${data.revenue.toFixed(2)}</p>
                          <p>Trips: {data.count}</p>
                          <p>Avg: ${data.avgRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RevenueBreakdownReport;
